import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { DOBDatePicker } from '../ui/date-picker';
import { toast } from 'sonner';
import { Eye, EyeOff, AlertCircle, HelpCircle, Shield } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import SimpleForgotPasswordModal from './SimpleForgotPasswordModal';
import { useAuth } from '../../context/AuthContext';
import { TwoFactorVerifyModal, TwoFactorSetupModal } from './TwoFactorAuth';
import TwoFactorRecoveryModal from './TwoFactorRecovery';
import { useRecaptcha } from '../../hooks/useRecaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// After a successful login/signup, land on the `?next=` path if the user
// arrived via a protected-route redirect. Same-origin, path-only guard
// (matches the sanitizer in HomePage).
const getPostLoginPath = () => {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('next');
  if (raw && typeof raw === 'string') {
    const t = raw.trim();
    if (t.startsWith('/') && !t.startsWith('//') && !/[:\\]/.test(t)) {
      return t;
    }
  }
  return '/dashboard';
};

const AuthModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [signupStep, setSignupStep] = useState(1);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // reCAPTCHA hooks for login and signup
  const { executeRecaptcha: executeLoginRecaptcha, isEnabled: loginRecaptchaEnabled, resetRecaptcha: resetLoginRecaptcha } = useRecaptcha('login');
  const { executeRecaptcha: executeSignupRecaptcha, isEnabled: signupRecaptchaEnabled, resetRecaptcha: resetSignupRecaptcha } = useRecaptcha('signup');
  
  // 2FA state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [show2FARecovery, setShow2FARecovery] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);  // Mandatory 2FA setup for admins
  const [pendingLoginData, setPendingLoginData] = useState(null);

  // Login state
  const [loginData, setLoginData] = useState({
    indos_number: '',
    password: ''
  });

  // Signup Step 1 state
  const [signupData, setSignupData] = useState({
    name: '',
    indos_number: '',
    email: '',
    mobile_number: '+91',
    date_of_birth: '',
    password: '',
    confirm_password: ''
  });

  // Signup Step 2 state - Security questions
  const [selectedQuestions, setSelectedQuestions] = useState([
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' }
  ]);

  // Terms and Privacy Policy agreement state
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  // Fetch security questions on mount
  useEffect(() => {
    if (open) {
      fetchSecurityQuestions();
    }
  }, [open]);

  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`);
      setSecurityQuestions(response.data.questions);
    } catch (error) {
      toast.error('Failed to load security questions');
    }
  };

  // Login handlers
  const handleLoginChange = (field, value) => {
    setLoginData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    const indosPattern = /^[0-9]{2}[A-Za-z]{2}[0-9]{4}$/;
    if (!indosPattern.test(loginData.indos_number)) {
      toast.error('Invalid INDoS number format');
      return;
    }

    setLoading(true);
    try {
      // Execute reCAPTCHA if enabled
      let recaptchaToken = null;
      if (loginRecaptchaEnabled) {
        recaptchaToken = await executeLoginRecaptcha();
        if (!recaptchaToken) {
          toast.error('Please complete the security verification');
          setLoading(false);
          return;
        }
      }
      
      const response = await axios.post(`${API}/auth/new-login`, {
        ...loginData,
        recaptcha_token: recaptchaToken
      });
      const { access_token, user, session_id, requires_2fa, two_factor_pending, needs_2fa_setup } = response.data;
      
      // Reset reCAPTCHA after successful submission
      resetLoginRecaptcha();
      
      // Store token and set axios headers (needed for 2FA verification/setup)
      localStorage.setItem('token', access_token);
      // Persist session_id so profile + admin session management can flag
      // "this is your current session". For 2FA users the real session
      // will be issued by /auth/2fa/verify (see below) — we overwrite then.
      if (session_id) {
        localStorage.setItem('session_id', session_id);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Check if 2FA verification is required (already has 2FA enabled)
      if (requires_2fa || two_factor_pending) {
        setPendingLoginData({ access_token, user });
        setShow2FAModal(true);
        setLoading(false);
        return;
      }
      
      // Check if admin/sub-admin needs to setup 2FA (mandatory)
      if (needs_2fa_setup) {
        setPendingLoginData({ access_token, user });
        setShow2FASetup(true);
        toast.info('Two-factor authentication is required. Please set it up now.');
        setLoading(false);
        return;
      }
      
      // No 2FA needed - complete login (token is already stored above)
      toast.success('Login successful!');
      onClose();
      
      // Force full page reload to properly initialize auth state
      window.location.href = getPostLoginPath();
    } catch (error) {
      resetLoginRecaptcha();
      // Handle error - detail can be a string or an object
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        // Object error (e.g., session limit exceeded)
        toast.error(errorDetail.message || 'Login failed');
      } else {
        toast.error(errorDetail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // 2FA handlers
  const handle2FASuccess = (newAccessToken) => {
    // TwoFactorVerifyModal already swapped the token in localStorage +
    // axios headers. Trust that, fall back to the value we just stored.
    const access_token = newAccessToken || localStorage.getItem('token');
    if (access_token) {
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    }
    
    setShow2FAModal(false);
    setPendingLoginData(null);
    toast.success('Login successful!');
    onClose();
    
    // Force full page reload to properly initialize auth state (same as non-2FA login)
    window.location.href = getPostLoginPath();
  };

  const handle2FACancel = () => {
    // Clear stored token since user cancelled
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setShow2FAModal(false);
    setPendingLoginData(null);
  };

  const handle2FARecover = () => {
    setShow2FAModal(false);
    setShow2FARecovery(true);
  };

  const handleRecoverySuccess = () => {
    setShow2FARecovery(false);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setPendingLoginData(null);
    toast.success('2FA disabled. Please login again.');
  };

  // Mandatory 2FA setup success handler
  const handle2FASetupSuccess = () => {
    if (pendingLoginData) {
      const { access_token } = pendingLoginData;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    }
    setShow2FASetup(false);
    setPendingLoginData(null);
    toast.success('2FA setup complete! Welcome.');
    onClose();
    window.location.href = getPostLoginPath();
  };

  // Cancel mandatory 2FA setup - must logout since they can't proceed without 2FA
  const handle2FASetupCancel = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setShow2FASetup(false);
    setPendingLoginData(null);
    toast.error('2FA setup is mandatory. You have been logged out.');
  };

  // Signup handlers
  const handleSignupChange = (field, value) => {
    setSignupData(prev => ({ ...prev, [field]: value }));
  };

  const validateSignupStep1 = () => {
    const indosPattern = /^[0-9]{2}[A-Za-z]{2}[0-9]{4}$/;
    if (!indosPattern.test(signupData.indos_number)) {
      toast.error('Invalid INDoS number format. Expected: NNLLNNNN (2 digits, 2 letters, 4 digits)');
      return false;
    }

    const mobilePattern = /^\+91[6-9][0-9]{9}$/;
    if (!mobilePattern.test(signupData.mobile_number)) {
      toast.error('Invalid mobile number. Expected: +91XXXXXXXXXX');
      return false;
    }

    if (signupData.password !== signupData.confirm_password) {
      toast.error('Passwords do not match');
      return false;
    }

    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return false;
    }

    return true;
  };

  const handleSignupStep1 = async (e) => {
    e.preventDefault();
    
    if (!validateSignupStep1()) return;

    setLoading(true);
    try {
      // Execute reCAPTCHA if enabled
      let recaptchaToken = null;
      if (signupRecaptchaEnabled) {
        recaptchaToken = await executeSignupRecaptcha();
        if (!recaptchaToken) {
          toast.error('Please complete the security verification');
          setLoading(false);
          return;
        }
      }
      
      await axios.post(`${API}/auth/new-signup-step1`, {
        ...signupData,
        recaptcha_token: recaptchaToken
      });
      toast.success('Step 1 completed! Now set up your security questions.');
      setSignupStep(2);
    } catch (error) {
      resetSignupRecaptcha();
      toast.error(error.response?.data?.detail || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...selectedQuestions];
    updated[index][field] = value;
    setSelectedQuestions(updated);
  };

  const validateSignupStep2 = () => {
    const questions = selectedQuestions.map(q => q.question);
    const uniqueQuestions = new Set(questions);
    
    if (questions.some(q => !q)) {
      toast.error('Please select all 5 security questions');
      return false;
    }

    if (uniqueQuestions.size !== 5) {
      toast.error('Please select 5 different security questions');
      return false;
    }

    if (selectedQuestions.some(q => !q.answer.trim())) {
      toast.error('Please answer all security questions');
      return false;
    }

    return true;
  };

  const handleSignupStep2 = async (e) => {
    e.preventDefault();
    
    if (!validateSignupStep2()) return;

    setLoading(true);
    try {
      // Execute reCAPTCHA if enabled (use same token as step 1, or get new one)
      let recaptchaToken = null;
      if (signupRecaptchaEnabled) {
        recaptchaToken = await executeSignupRecaptcha();
        if (!recaptchaToken) {
          toast.error('Please complete the security verification');
          setLoading(false);
          return;
        }
      }
      
      const payload = {
        ...signupData,
        security_questions: selectedQuestions,
        recaptcha_token: recaptchaToken
      };
      
      const response = await axios.post(`${API}/auth/new-signup-complete`, payload);
      const { access_token, user } = response.data;
      
      // Reset reCAPTCHA after successful submission
      resetSignupRecaptcha();
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('Account created successfully!');
      onClose();
      window.location.reload();
    } catch (error) {
      resetSignupRecaptcha();
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableQuestions = (currentIndex) => {
    const selected = selectedQuestions
      .map((q, idx) => idx !== currentIndex ? q.question : null)
      .filter(Boolean);
    return securityQuestions.filter(q => !selected.includes(q));
  };

  return (
    <Dialog open={open && !show2FAModal && !show2FARecovery && !show2FASetup} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-[#0A7EA4]">
            Welcome to MARQ
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="login" className="w-full" onValueChange={() => setSignupStep(1)}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login">
            <form
              onSubmit={handleLogin}
              onKeyDown={(e) => {
                // Belt-and-suspenders: some password managers intercept the
                // first Enter press to dismiss their autofill dropdown, which
                // eats the implicit form submit. Explicitly submit on Enter
                // so the login is always reliable.
                if (e.key === 'Enter' && !loading) {
                  e.preventDefault();
                  handleLogin(e);
                }
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="login-indos">
                  INDoS Number
                  <span className="text-xs text-gray-500 ml-1">(8 characters)</span>
                </Label>
                <Input
                  id="login-indos"
                  type="text"
                  value={loginData.indos_number}
                  onChange={(e) => handleLoginChange('indos_number', e.target.value.toUpperCase())}
                  required
                  className="mt-1"
                  placeholder="Enter your INDoS"
                  maxLength={8}
                />
              </div>

              <div>
                <Label htmlFor="login-password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => handleLoginChange('password', e.target.value)}
                    required
                    className="pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  setShowForgotPassword(true);
                }}
                className="text-sm text-[#0A7EA4] hover:underline"
              >
                Forgot Password?
              </button>

              <Button
                type="submit"
                className="w-full bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              {/* reCAPTCHA indicator */}
              {loginRecaptchaEnabled && (
                <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
                  <Shield size={12} className="text-green-600" />
                  <span>Protected by reCAPTCHA</span>
                </div>
              )}

              <div className="text-center pt-2">
                <Link 
                  to="/contact-us" 
                  onClick={onClose}
                  className="text-sm text-gray-500 hover:text-[#0A7EA4] inline-flex items-center gap-1"
                >
                  <HelpCircle size={14} />
                  Need help? Contact us
                </Link>
              </div>
            </form>
          </TabsContent>

          {/* Signup Tab */}
          <TabsContent value="signup">
            {signupStep === 1 && (
              <form onSubmit={handleSignupStep1} className="space-y-4">
                <p className="text-sm text-gray-600 text-center mb-4">
                  Step 1 of 2: Basic Information
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="signup-name">Full Name *</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={signupData.name}
                      onChange={(e) => handleSignupChange('name', e.target.value)}
                      required
                      className="mt-1"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <Label htmlFor="signup-indos">
                      INDoS Number *
                      <span className="text-xs text-gray-500 ml-1">(8 characters)</span>
                    </Label>
                    <Input
                      id="signup-indos"
                      type="text"
                      value={signupData.indos_number}
                      onChange={(e) => handleSignupChange('indos_number', e.target.value.toUpperCase())}
                      required
                      className="mt-1"
                      placeholder="Enter your INDoS"
                      maxLength={8}
                    />
                  </div>

                  <div>
                    <Label htmlFor="signup-email">Email Address *</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={signupData.email}
                      onChange={(e) => handleSignupChange('email', e.target.value)}
                      required
                      className="mt-1"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="signup-mobile">
                      Mobile Number *
                      <span className="text-xs text-gray-500 ml-1">(+91XXXXXXXXXX)</span>
                    </Label>
                    <Input
                      id="signup-mobile"
                      type="tel"
                      value={signupData.mobile_number}
                      onChange={(e) => handleSignupChange('mobile_number', e.target.value)}
                      required
                      className="mt-1"
                      placeholder="+919876543210"
                      maxLength={13}
                    />
                  </div>

                  <div>
                    <Label htmlFor="signup-dob">Date of Birth *</Label>
                    <div className="mt-1">
                      <DOBDatePicker
                        date={signupData.date_of_birth ? new Date(signupData.date_of_birth) : null}
                        onDateChange={(date) => handleSignupChange('date_of_birth', date ? format(date, 'yyyy-MM-dd') : '')}
                        placeholder="Select your date of birth"
                      />
                    </div>
                  </div>

                  <div></div>

                  <div>
                    <Label htmlFor="signup-password">Password *</Label>
                    <div className="relative mt-1">
                      <Input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        value={signupData.password}
                        onChange={(e) => handleSignupChange('password', e.target.value)}
                        required
                        className="pr-10"
                        placeholder="••••••••"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="signup-confirm-password">Confirm Password *</Label>
                    <div className="relative mt-1">
                      <Input
                        id="signup-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={signupData.confirm_password}
                        onChange={(e) => handleSignupChange('confirm_password', e.target.value)}
                        required
                        className="pr-10"
                        placeholder="••••••••"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Terms and Privacy Agreement */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-start space-x-2">
                    <Checkbox 
                      id="terms" 
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      className="mt-0.5"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600 leading-tight cursor-pointer">
                      I agree to the{' '}
                      <Link 
                        to="/terms-of-use" 
                        onClick={onClose}
                        className="text-[#0A7EA4] hover:underline"
                      >
                        Terms of Use
                      </Link>
                    </label>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Checkbox 
                      id="privacy" 
                      checked={agreedToPrivacy}
                      onCheckedChange={setAgreedToPrivacy}
                      className="mt-0.5"
                    />
                    <label htmlFor="privacy" className="text-sm text-gray-600 leading-tight cursor-pointer">
                      I agree to the{' '}
                      <Link 
                        to="/privacy-policy" 
                        onClick={onClose}
                        className="text-[#0A7EA4] hover:underline"
                      >
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#14B8A6] hover:bg-[#14B8A6]/90"
                  disabled={loading || !agreedToTerms || !agreedToPrivacy}
                >
                  {loading ? 'Validating...' : 'Next: Security Questions'}
                </Button>

                {/* reCAPTCHA indicator for signup */}
                {signupRecaptchaEnabled && (
                  <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
                    <Shield size={12} className="text-green-600" />
                    <span>Protected by reCAPTCHA</span>
                  </div>
                )}

                <div className="text-center">
                  <Link 
                    to="/contact-us" 
                    onClick={onClose}
                    className="text-sm text-gray-500 hover:text-[#0A7EA4] inline-flex items-center gap-1"
                  >
                    <HelpCircle size={14} />
                    Need help? Contact us
                  </Link>
                </div>
              </form>
            )}

            {signupStep === 2 && (
              <form onSubmit={handleSignupStep2} className="space-y-4">
                <p className="text-sm text-gray-600 text-center mb-2">
                  Step 2 of 2: Security Questions
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="text-amber-600 mt-0.5" size={18} />
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> Keep these answers secret and remember them.
                      They will be used for password recovery.
                    </p>
                  </div>
                </div>

                {selectedQuestions.map((item, index) => (
                  <div key={index} className="space-y-2 p-3 border rounded-lg bg-gray-50">
                    <Label className="text-sm font-medium">Security Question {index + 1} *</Label>
                    <Select
                      value={item.question}
                      onValueChange={(value) => handleQuestionChange(index, 'question', value)}
                      required
                    >
                      <SelectTrigger className="bg-white w-full h-auto min-h-[40px] whitespace-normal text-left">
                        <SelectValue placeholder="Select a question" className="whitespace-normal" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[500px]">
                        {getAvailableQuestions(index).map((question) => (
                          <SelectItem key={question} value={question} className="text-xs whitespace-normal py-2">
                            {question}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {item.question && (
                      <Input
                        type="text"
                        value={item.answer}
                        onChange={(e) => handleQuestionChange(index, 'answer', e.target.value)}
                        required
                        className="bg-white w-full"
                        placeholder="Your answer"
                      />
                    )}
                  </div>
                ))}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSignupStep(1)}
                    className="flex-1"
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#14B8A6] hover:bg-[#14B8A6]/90"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </div>
              </form>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>

      <SimpleForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSuccess={() => {
          setShowForgotPassword(false);
          // Optionally open login modal again
        }}
      />

      {/* 2FA Verification Modal */}
      <TwoFactorVerifyModal
        isOpen={show2FAModal}
        onClose={handle2FACancel}
        onSuccess={handle2FASuccess}
        onRecover={handle2FARecover}
      />

      {/* 2FA Recovery Modal */}
      <TwoFactorRecoveryModal
        isOpen={show2FARecovery}
        onClose={() => setShow2FARecovery(false)}
        onSuccess={handleRecoverySuccess}
        defaultIndos={loginData.indos_number}
      />

      {/* Mandatory 2FA Setup Modal for Admins */}
      <TwoFactorSetupModal
        isOpen={show2FASetup}
        onClose={handle2FASetupCancel}
        onSuccess={handle2FASetupSuccess}
        mandatory={true}
      />
    </Dialog>
  );
};

export default AuthModal;